// Redux
import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from '@redux/configureStore';
import { getDynamicData, setLoaderRegisterCompany, showLoader } from '@redux/utils/utils.slice';
import { setPlansEmployee } from '@redux/plans/plans.slice';
// Types
import { IOnboardingState } from './types';
// Components
import { Routes } from '@components/paths';
// Api
import { apiPostSaveStep, apiGetPendingDocuments, apiPostPendingDocuments, apiPostGetDueDiligenceStatus } from '@api/onboarding';
import { apiPostSupportTransaction } from '@api/plans';
// Constants
import { NOMINA_EN_LINEA_PRODUCT, OnboardingStepsUser, TypeDocumentNames } from '@constants/Onboarding';
// Models
import { IApproveDocumentsParams, IDueDiligenceStatusResposne } from '@models/Onboarding';
// Utils
import { getRoute } from '@utils/Paths';
import { IPaymentResponse } from '@redux/plans/types';
import { setPlan } from '@redux/company/company.slice';
import { ICompanyPlan } from '@models/Plans';
import { Roles } from '@constants/Session';
import { DueDiligenceStatus } from '@constants/DueDiligence';
import { OnboardingResponseCodes } from '@constants/responseCodes/onboarding';

const initialState: IOnboardingState = {
    dataProcessing: [],
    dueDiligence: '',
    urlEncrypt: '',
    step: null,
    userId: '',
    modalErrorRequest: null,
    error: '',
    isNewCompany: false,
};

const onboardingSlide = createSlice({
    name: 'onboarding',
    initialState,
    reducers: {
        setDataProcessing: (state, action) => {
            state.dataProcessing = action.payload;
        },
        setDueDiligence: (state, action) => {
            state.dueDiligence = action.payload;
        },
        setStep: (state, action) => {
            state.step = action.payload;
        },
        setUserId: (state, action) => {
            state.userId = action.payload;
        },
        setUrlEncrypt: (state, action) => {
            state.urlEncrypt = action.payload;
        },
        setModalErroRequest: (state, action) => {
            state.modalErrorRequest = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },

        setIsNewCompany: (state, action) => {
            state.isNewCompany = action.payload;
        },
    },
});

export const checkCompanyOnboardingInformation = (
    documentType: string,
    documentNumber: string,
    navigate: (route: string) => void
): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            company: { information },
            utils: { document_type },
        } = getState();
        try {
            const employerUser = information?.users ? information?.users[0].rol : 'user';
            dispatch(setModalErroRequest(false));
            if (employerUser === Roles.SUPPORT) {
                const supportTransactionResponse = await apiPostSupportTransaction<IPaymentResponse>({});
                if (!supportTransactionResponse.data) {
                    dispatch(setModalErroRequest(true));
                    return;
                }
                dispatch(setDataProcessing([]));
                dispatch(setPlansEmployee([supportTransactionResponse.data]));
                dispatch(setPlan((supportTransactionResponse.data as ICompanyPlan) || {}));
                dispatch(saveUserStep(OnboardingStepsUser.HOME));
                navigate(getRoute(Routes.HOME));
                return;
            }
            const getPendingDocumentResponse = await apiGetPendingDocuments({
                documentType,
                documentNumber,
            });

            if (!getPendingDocumentResponse || !getPendingDocumentResponse.data) {
                dispatch(setStep(OnboardingStepsUser.POLITICAL_DATA));
                dispatch(setModalErroRequest(true));
                dispatch(showLoader(false));
                return;
            }
            dispatch(setDataProcessing(getPendingDocumentResponse.data));
            const dataProcessingDocumentIsPending = getPendingDocumentResponse.data.some(
                document =>
                    document.documentName === TypeDocumentNames.DATA_PROCESSING && document.product === NOMINA_EN_LINEA_PRODUCT
            );
            if (dataProcessingDocumentIsPending) {
                dispatch(setStep(OnboardingStepsUser.POLITICAL_DATA));
                navigate(getRoute(Routes.HOME));
                return;
            }
            if (document_type.length == 0) dispatch(getDynamicData(['document_type']));
            dispatch(saveUserStep(OnboardingStepsUser.HOME));
        } catch (error) {
            dispatch(setLoaderRegisterCompany(false));
            dispatch(showLoader(false));
            dispatch(setError(error));
            dispatch(setModalErroRequest(true));
            dispatch(setIsNewCompany(false));
        }
    };
};

export const saveUserStep = (step: number, action?: () => void): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            onboarding: { userId },
        } = getState();
        try {
            await apiPostSaveStep({
                step,
                user_id: userId,
            });
            dispatch(setStep(step));
            action && action();
        } catch (error) {
            dispatch(setError(error));
        }
    };
};

export const getAllPendingDocumentsToBeApprove = (documentType = '', documentNumber = ''): AppThunk => {
    return async (dispatch): Promise<void> => {
        const pendingDocumentsResponse = await apiGetPendingDocuments({
            documentType,
            documentNumber,
        });

        if (!pendingDocumentsResponse || !pendingDocumentsResponse.data) {
            dispatch(showLoader(false));
            dispatch(setModalErroRequest(true));
            return;
        }
        dispatch(setDataProcessing(pendingDocumentsResponse.data));
        dispatch(setModalErroRequest(false));
        dispatch(showLoader(false));
    };
};

export const approvePendingDocuments = (approveInformation: IApproveDocumentsParams): AppThunk<Promise<boolean>> => {
    return async (dispatch): Promise<boolean> => {
        const pendingDocumentsResponse = await apiPostPendingDocuments(approveInformation);
        if (pendingDocumentsResponse.code != OnboardingResponseCodes.SUCCESS_OPERATION) {
            dispatch(setLoaderRegisterCompany(false));
            dispatch(showLoader(false));
            dispatch(setModalErroRequest(true));
            return false;
        }
        return true;
    };
};

export const getCompanyDueDiligenceStatus = (
    companyName: string,
    documentType: string,
    documentNumber: string
): AppThunk<Promise<IDueDiligenceStatusResposne>> => {
    return async (dispatch): Promise<IDueDiligenceStatusResposne> => {
        const dueDiligenceStatus = await apiPostGetDueDiligenceStatus({
            companyName,
            documentType,
            documentNumber,
        });

        if (!dueDiligenceStatus || !dueDiligenceStatus.data) {
            dispatch(setDueDiligence(DueDiligenceStatus.ERROR));
            dispatch(setUrlEncrypt(''));
            return {
                dueDiligenceStatus: DueDiligenceStatus.ERROR,
                url: '',
            };
        }
        dispatch(setDueDiligence(dueDiligenceStatus.data.dueDiligenceStatus));
        dispatch(setUrlEncrypt(`${process.env.REACT_APP_FORM_DILIGENCE_URL}/${dueDiligenceStatus.data.url}`));
        return {
            dueDiligenceStatus: dueDiligenceStatus.data.dueDiligenceStatus,
            url: `${process.env.REACT_APP_FORM_DILIGENCE_URL}/${dueDiligenceStatus.data.url}`,
        };
    };
};

export const {
    setDataProcessing,
    setDueDiligence,
    setStep,
    setUserId,
    setUrlEncrypt,
    setModalErroRequest,
    setIsNewCompany,
    setError,
} = onboardingSlide.actions;

export default onboardingSlide.reducer;
